import { useRef } from 'react';
import { motion, useScroll, useTransform, useMotionTemplate } from 'framer-motion';

// Simple album landing page with a hero video that parallax-blurs on scroll,
// followed by Spotify embeds and quick links. Optimized for iPhone Safari.


const VIDEO_SRC = '/mp4/ll-preview.mp4'; // Replace with final CDN path if needed
// Update these Spotify embeds with the final album/playlist IDs when available
// https://open.spotify.com/album/3cFI8nfDFwBGdRbKpOPHKx?si=egv9D0rOT7i12mLBtMZoTQ
const SPOTIFY_EMBED_ARTIST =
  'https://open.spotify.com/embed/album/0wMC6I2G3sKvhcsjn8N1Kd?utm_source=generator&theme=0';
const SPOTIFY_EMBED_PLAYLIST = 'https://open.spotify.com/embed/playlist/3K9i5nZANkK7ijOfhzoDJu?utm_source=generator&theme=0';


// Inline brand icons (currentColor-aware) for colorful platform chips
const SpotifyIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" className="block">
    <path
      fill="currentColor"
      d="M12 1.5C6.2 1.5 1.5 6.2 1.5 12S6.2 22.5 12 22.5 22.5 17.8 22.5 12 17.8 1.5 12 1.5zm5 15.3c-.2.3-.6.4-.9.2-2.4-1.5-5.4-1.9-9-1-.3.1-.7-.1-.8-.4-.1-.3.1-.7.4-.8 3.9-1 7.3-.6 10 1.1.3.2.4.6.3.9zm1.3-3.1c-.2.3-.6.4-1 .2-2.8-1.7-7.2-2.2-10.6-1.1-.3.1-.7-.1-.8-.4-.1-.3.1-.7.5-.8 3.9-1.2 8.8-.7 12 1.3.3.2.4.6.3.8zM19 10c-.2.3-.6.4-1 .2-3.3-2-9-2.2-12.2-1.1-.3.1-.7-.1-.8-.4-.1-.3.1-.7.4-.8 3.7-1.2 9.9-1 13.6 1.2.3.2.4.6.2.9z"
    />
  </svg>
);

const AppleMusicIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" className="block">
    <path
      fill="currentColor"
      d="M16.365 2.012c.123 1.28-.38 2.57-1.19 3.51-.807.94-2.112 1.66-3.372 1.56-.149-1.24.44-2.55 1.223-3.45.823-.96 2.236-1.67 3.339-1.62zM20.9 17.04c-.065 1.44.57 2.83 1.242 3.78-.906.52-1.99.99-3.156 1.01-1.384.02-1.985-.45-3.01-.45-1.025 0-1.69.44-3.035.47-1.216.03-2.14-.56-3.05-1.07-1.67-.95-3.12-2.71-3.54-4.84-.747-3.78 1.706-7.67 5.185-7.72 1.206-.02 2.248.49 3.01.49.764 0 2.077-.6 3.5-.52.596.02 2.272.24 3.35 1.8-.087.053-2.002 1.172-1.946 3.503.05 2.79 2.34 3.7 2.4 3.74z"
    />
  </svg>
);

const YouTubeMusicIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" className="block">
    <path fill="currentColor" d="M12 2C6.486 2 2 6.486 2 12s4.486 10 10 10 10-4.486 10-10S17.514 2 12 2zm-3 6.5l8 4.5-8 4.5v-9z" />
  </svg>
);

const DeezerIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" className="block">
    <path fill="currentColor" d="M3 15h3v3H3v-3zm4-4h3v7H7v-7zm4-2h3v9h-3V9zm4-3h3v12h-3V6zm4 1h3v11h-3V7z" />
  </svg>
);

const TidalIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" className="block">
    <path fill="white" d="M6 8l2 2-2 2-2-2 2-2zm6-4l2 2-2 2-2-2 2-2zm0 8l2 2-2 2-2-2 2-2zm6-4l2 2-2 2-2-2 2-2z" />
  </svg>
);
// Clothing icon (make it white)

const PosterboyzShopIcon = () => (
  <svg width="40px" height="40px" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" fill="white">
  <title>clothes</title>
  <g id="Layer_2" data-name="Layer 2">
    <g id="invisible_box" data-name="invisible box">
      <rect width="48" height="48" fill="none"/>
    </g>
    <g id="icons_Q2" data-name="icons Q2">
      <path d="M45.2,12.9,37.1,4H10.9L2.8,12.9a3,3,0,0,0,.1,4.2l7.5,7.5V44H37.7V24.6l7.5-7.5A3,3,0,0,0,45.2,12.9Zm-8.4,6.8-3.1-2.9V40H14.3V16.8l-3.1,2.9L6.4,14.9,12.7,8h7.4a4,4,0,0,0,7.8,0h7.4l6.3,6.9Z"/>
    </g>
  </g>
</svg>
);

type PlatformDef = {
  name: string;
  href: string;
  color: string;
  Icon: () => JSX.Element;
};

const PLATFORM_LINKS: PlatformDef[] = [
  { name: 'Spotify', href: 'https://open.spotify.com/album/0wMC6I2G3sKvhcsjn8N1Kd', color: '#1DB954', Icon: SpotifyIcon },
  { name: 'Apple Music', href: 'http://itunes.apple.com/album/id1767867548?ls=1&app=itunes', color: '#FC3C44', Icon: AppleMusicIcon },
  { name: 'YouTube Music', href: 'https://www.youtube.com/@BarankaMusic', color: '#FF0033', Icon: YouTubeMusicIcon },
  { name: 'Deezer', href: 'https://www.deezer.com/en/album/650626771', color: '#FF0000', Icon: DeezerIcon },
  { name: 'Tidal', href: 'https://tidal.com/album/386768281/u', color: '#0A0A0A', Icon: TidalIcon },
  { name: 'Posterboyz Shop', href: 'https://shop.posterboyz.studio', color: '#FE5000', Icon: PosterboyzShopIcon },
];

export default function AlbumVideo() {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const { scrollYProgress } = useScroll({
    target: wrapperRef,
    offset: ['start start', 'end start'],
  });

  // Smooth, subtle parallax and blur tied to the hero section only
  const y = useTransform(scrollYProgress, [0, 1], [0, -220]);
  const opacity = useTransform(scrollYProgress, [0, 0.4, 1], [1, 0.88, 0.7]);
  // Start blur only after ~30% progress so the first part stays crisp
  const blurVal = useTransform(scrollYProgress, [0, 0.49, 1], [0, 0, 10]);
  const filter = useMotionTemplate`blur(${blurVal}px)`;
  // Arrow visibility: visible at start, fade as we approach the 60% snap point
  const arrowOpacity = useTransform(scrollYProgress, [0, 0.1, 0.2], [1, 1, 0]);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Hero video - sticky background with smooth parallax */}
      <section ref={wrapperRef} className="relative h-[160vh]">
        <div className="sticky top-0 h-[90vh] sm:h-screen overflow-hidden z-0">
          <motion.div
            className="absolute inset-0 will-change-transform"
            style={{ y, opacity, filter }}
            layout
          >
            <video
              className="absolute inset-0 w-full h-full object-cover"
              autoPlay
              muted
              loop
              playsInline
              // @ts-ignore vendor prop for legacy iOS
              webkit-playsinline="true"
              preload="auto"
              controls={false}
              style={{ transform: 'translateZ(0)' }}
            >
              <source src={VIDEO_SRC} type="video/mp4" />
            </video>
            <div className="absolute inset-0 bg-linear-to-b from-black/30 via-black/20 to-black" />
          </motion.div>

          {/* Bottom title overlay */}
          <div className="absolute bottom-4 left-0 right-0 px-4">
            <div className="max-w-screen-sm mx-auto">
              <motion.h1
                initial={{ y: 12, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.15 }}
                className="text-3xl font-bold"
              >
                Leren Leven
              </motion.h1>
              <motion.p
                initial={{ y: 12, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.25 }}
                className="text-zinc-300"
              >
                Fresku
              </motion.p>
            </div>
          </div>

        </div>
        {/* Simple down-arrow scroll indicator (fixed to viewport bottom to avoid layout shifts) */}
        <div className="fixed left-0 right-0 bottom-3 md:bottom-6 z-20 flex justify-center pointer-events-none">
          <motion.button
            type="button"
            whileHover={{ scale: 1.06 }}
            whileTap={{ scale: 0.96 }}
            onClick={() => {
              const base = wrapperRef.current?.offsetTop ?? 0;
              const target = base + window.innerHeight * 0.6; // keep ~40% of hero visible
              window.scrollTo({ top: target, behavior: 'smooth' });
            }}
            aria-label="Scroll down"
            className="pointer-events-auto"
            style={{ opacity: arrowOpacity }}
          >
            <motion.svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              className="w-6 h-6 md:w-7 md:h-7 text-white/90"
              animate={{ y: [0, 6, 0] }}
              transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
            >
              <path d="M6 9l6 6 6-6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </motion.svg>
          </motion.button>
        </div>
      </section>

      {/* Content body */}
      <main ref={contentRef} className="relative z-10 px-4 pb-20 -mt-[40vh] sm:-mt-[42vh]">
        <div className="max-w-screen-sm mx-auto space-y-8">
          {/* First embed card */}
          <motion.section
            layout
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.06 }}
            transition={{ duration: 0.3 }}
            className="bg-zinc-900 rounded-2xl p-4"
          >
            <h2 className="text-2xl font-bold mb-1">Leren Leven</h2>
            <p className="text-zinc-400 mb-4">Fresku</p>
            <div className="rounded-xl overflow-hidden">
              <iframe
                title="Spotify artist"
                src={SPOTIFY_EMBED_ARTIST}
                width="100%"
                height="352"
                frameBorder="0"
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
                style={{ borderRadius: 12 }}
              />
            </div>
          </motion.section>

          {/* Second embed card */}
          <motion.section
            layout
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.06 }}
            transition={{ duration: 0.3 }}
            className="bg-zinc-900 rounded-2xl p-4"
          >
            <h2 className="text-2xl font-bold mb-1">Fresku’s</h2>
            <p className="text-zinc-400 mb-4">personal playlist</p>
            <div className="rounded-xl overflow-hidden">
              <iframe
                title="Spotify playlist"
                src={SPOTIFY_EMBED_PLAYLIST}
                width="100%"
                height="352"
                frameBorder="0"
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
                style={{ borderRadius: 12 }}
              />
            </div>
          </motion.section>

          {/* Links section */}
          <motion.section
            layout
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.06 }}
            transition={{ duration: 0.3 }}
            className="bg-zinc-900 rounded-2xl p-4"
          >
            <h2 className="text-3xl font-bold mb-4">More links</h2>
            
            <div className="grid grid-cols-2 gap-3">
              {PLATFORM_LINKS.map(({ name, href, color, Icon }) => (
                <a
                  key={name}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 bg-black/40 hover:bg-black/60 transition-colors rounded-lg px-3 py-3 text-sm"
                  aria-label={`Open on ${name}`}
                >
                  <span className="inline-flex h-5 w-5 items-center justify-center" style={{ color }}>
                    <Icon />
                  </span>
                  <span>{name}</span>
                </a>
              ))}
            </div>
          </motion.section>
        </div>
      </main>
    </div>
  );
}

